import React, { useState, useEffect, useMemo } from 'react';
import { getPatInfo, createPat, revokePat } from '../services/api';

const AccessKeyManager = ({ getAccessTokenSilently, addToast }) => {
  const [patData, setPatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [newVisibleToken, setNewVisibleToken] = useState(null);
  const [expiration, setExpiration] = useState("30");
  const patButtonStyle = { marginTop: 0, whiteSpace: 'nowrap' };
  const destructiveButtonStyle = { marginTop: 0, whiteSpace: 'nowrap', backgroundColor: '#ef4444', color: 'white' };
  const warningStyle = { fontSize: '0.8em', color: '#ef4444', marginTop: '15px' };

  const fetchInfo = async () => {
    try {
      setLoading(true);
      const data = await getPatInfo(getAccessTokenSilently);

      if (data && data.exists === true) {
        setPatData({
          exists: true,
          name: data.name,
          created_at: data.createdAt,
          expires_at: data.expiresAt,
          last_used_at: data.lastUsedAt,
        });
      } else {
        setPatData({ exists: false });
      }
    } catch (err) {
      console.error("Error fetching PAT info:", err);
      setPatData({ exists: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleString();
  };

  const expirationDatePreview = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + parseInt(expiration, 10));
    return d.toLocaleString();
  }, [expiration]);

  const handleCreate = async () => {
    try {
      setLoading(true);
      const result = await createPat(getAccessTokenSilently, { expirationDays: expiration });

      setNewVisibleToken(result.token);

      setPatData({
        exists: true,
        name: result.name || "openApiKey",
        created_at: result.createdAt || result.created_at || new Date().toISOString(),
        expires_at: result.expiresAt || result.expires_at,
        last_used_at: result.lastUsedAt || result.last_used_at || null,
      });

      addToast("API Key generated successfully", "success");
      await fetchInfo();
      setShowForm(false);
    } catch (err) {
      addToast("Error: " + (err.message || err), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    try {
      setLoading(true);
      await revokePat(getAccessTokenSilently);
      setPatData({ exists: false });
      setShowRevokeConfirm(false);
      setNewVisibleToken(null);
    } catch (err) {
      addToast("Error revoking key", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !patData) return <div className="info-message">Loading Access Key status...</div>;

  return (
    <div className="pat-manager">
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: 0, whiteSpace: 'nowrap' }}>Personal Access Token (PAT)</h3>

        {!showForm && !showRevokeConfirm && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px', marginTop: '15px' }}>
            <button
              onClick={() => {
                setShowRevokeConfirm(false);
                setShowForm(true);
              }}
              className="copy-token-btn"
              style={patButtonStyle}
            >
              {patData?.exists ? "Update API Key" : "Create API Key"}
            </button>
            {patData?.exists && (
              <button
                onClick={() => setShowRevokeConfirm(true)}
                className="copy-token-btn"
                style={patButtonStyle}
              >
                Revoke Key
              </button>
            )}
          </div>
        )}
      </div>

      {newVisibleToken && (
        <div className="success-message" style={{ borderLeft: '4px solid #10b981', marginBottom: '20px', background: '#f0fdf4', position: 'relative' }}>
          <button 
            onClick={() => setNewVisibleToken(null)} 
            style={{ position: 'absolute', right: '10px', top: '10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.2em', color: '#166534' }}
          >×</button>
          <p style={{ color: '#166534', fontWeight: 'bold', margin: 0 }}>✓ Key Generated Successfully</p>
          <p style={{ fontSize: '0.9em', marginTop: 6 }}>Copy this key now. <strong>It will not be shown again.</strong></p>
          <div className="token-row" style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: 10 }}>
            <code className="token-preview" style={{ background: '#fff', border: '1px solid #bbf7d0', padding: '8px 12px' }}>{newVisibleToken}</code>
            <button className="copy-token-btn" onClick={() => {
              navigator.clipboard.writeText(newVisibleToken);
              addToast("Copied to clipboard!", "success");
            }}>Copy</button>
          </div>
        </div>
      )}

      {patData?.exists && !showForm && !showRevokeConfirm && (
        <div className="token-card">
          <div className="upc-row"><span className="upc-label">Name:</span> <span className="upc-value">{patData.name}</span></div>
          <div className="upc-row"><span className="upc-label">Created:</span> <span className="upc-value">{formatDate(patData.created_at)}</span></div>
          <div className="upc-row"><span className="upc-label">Expires:</span> <span className="upc-value">{formatDate(patData.expires_at)}</span></div>
          <div className="upc-row"><span className="upc-label">Last Used:</span> <span className="upc-value">{formatDate(patData.last_used_at)}</span></div>
        </div>
      )}

      {patData?.exists && showRevokeConfirm && (
        <div className="token-card pat-form" style={{ borderTop: '4px solid #ef4444' }}>
          <h4 style={{ marginTop: 0, color: '#ef4444' }}>Revoke API Key</h4>

          <div className="upc-row"><span className="upc-label">Name:</span> <span className="upc-value">{patData.name}</span></div>
          <div className="upc-row"><span className="upc-label">Created:</span> <span className="upc-value">{formatDate(patData.created_at)}</span></div>
          <div className="upc-row"><span className="upc-label">Expires:</span> <span className="upc-value">{formatDate(patData.expires_at)}</span></div>
          <div className="upc-row"><span className="upc-label">Last Used:</span> <span className="upc-value">{formatDate(patData.last_used_at)}</span></div>

          <p style={warningStyle}>
            * Clicking Revoke Immediately will invalidate your current Key and all applications using the current Key will need to be updated with the new key !!!
          </p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
            <button
              onClick={handleRevoke}
              disabled={loading}
              className="copy-token-btn"
              style={destructiveButtonStyle}
            >
              Revoke Immediately
            </button>
            <button
              onClick={() => setShowRevokeConfirm(false)}
              className="copy-token-btn"
              style={patButtonStyle}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!patData?.exists && !showForm && !showRevokeConfirm && (
        <div className="token-card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#666', marginBottom: '20px' }}>You don't have an active API Key.</p>
        </div>
      )}

      {showForm && (
        <div className="token-card pat-form" style={{ borderTop: patData?.exists ? '4px solid #ef4444' : '4px solid #007fc2' }}>
          <h4 style={patData?.exists ? { marginTop: 0, color: '#ef4444' } : { marginTop: 0 }}>{patData?.exists ? "Update API Key" : "Create New API Key"}</h4>

          <div className="form-group">
            <label>Name <span title="Always openApiKey" style={{ cursor: 'help' }}>ⓘ</span></label>
            <input type="text" value="openApiKey" disabled className="select-styled" style={{ opacity: 0.6, background: '#f9fafb' }} />
          </div>

          <div className="form-group" style={{ marginTop: '15px' }}>
            <label>Expiration <span>ⓘ</span></label>
            <select value={expiration} onChange={(e) => setExpiration(e.target.value)} className="select-styled">
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
              <option value="365">1 year</option>
            </select>
            <div className="info-message" style={{ fontSize: '0.85em', marginTop: '10px', background: '#fffbeb', padding: '8px' }}>
              Key will expire on: <strong>{expirationDatePreview}</strong>
            </div>
          </div>

          {patData?.exists && (
            <p style={warningStyle}>
              * Updating will invalidate your current key immediately and you will need to update all applications using the key with the new key
            </p>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
            <button
              onClick={handleCreate}
              disabled={loading}
              className="copy-token-btn"
              style={patData?.exists ? destructiveButtonStyle : patButtonStyle}
            >
              {patData?.exists ? "Update & Generate New" : "Create Key"}
            </button>
            <button onClick={() => setShowForm(false)} className="copy-token-btn" style={patButtonStyle}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessKeyManager;
